import React from "react";
import { ButtonToggle, ButtonToggleOption, Text, Tooltip } from "@showitinc/showit-wc";
import AppConstants from "../../../constants/AppConstants";
import TransformActions from "../../../actions/TransformActions";
import styles from "./ElementsProperties.module.css";
import UIActions from "../../../actions/UIActions";
import ElementPropertiesPositionPanel from "./ElementPropertiesPositionPanel";
import PropertyPanelSectionWrapper from "../../base/PropertyPanelSectionWrapper";

const {
  ALIGN_MIDDLE,
  ALIGN_RIGHT,
  ALIGN_LEFT,
  ALIGN_CENTER,
  ALIGN_TOP,
  ALIGN_BOTTOM,
  MATCH_SIZE_WIDTH,
  MATCH_SIZE_HEIGHT,
  MATCH_SIZE_BOTH,
  DISTRIBUTE_HORIZONTAL_SPACE,
  DISTRIBUTE_VERTICAL_SPACE,
  PANEL_SECTION_ELEMENTS_ALIGN,
} = AppConstants;

/**
 * @component ElementPropertiesAlignmentPanel
 * @description A React component that renders an alignment panel for element properties.
 * This panel provides various alignment and distribution options for selected elements
 * within a canvas or group.
 *
 * @param {Object} props - The component props
 * @param {string} props.activeSection - The currently active section in the properties panel
 * @param {string} props.contained - Selected elements are in aa group
 * @param {Object} props.viewState - The view state object
 *
 */
const ElementPropertiesAlignmentPanel = ({ viewState, contained }) => {
  const _align = (e) => {
    const alignPos = e?.detail?.value;
    alignPos && TransformActions.align(alignPos);
  };

  const _alignToCanvas = (e) => {
    const alignPos = e?.detail?.value;
    alignPos && TransformActions.alignToCanvas(alignPos);
  };

  const _matchSize = (e) => {
    const dimension = e?.detail?.value;
    dimension && TransformActions.matchSize(dimension);
  };

  const _distributeSpace = (e) => {
    const type = e?.detail?.value;
    type && TransformActions.distributeSpace(type);
  };

  const _togglePanelSection = (sectionName) => {
    UIActions.toggleElementsPropertiesSection({ name: sectionName });
  };

  const activeSection = viewState.get("activeSection");

  return (
    <PropertyPanelSectionWrapper
      title="Size & Position"
      className={styles.alignmentPanel}
      key="alignment"
      open={activeSection === PANEL_SECTION_ELEMENTS_ALIGN}
      onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENTS_ALIGN)}
    >
      {!contained && (
        <>
          <Text className="panel-subheading">Align to Canvas</Text>
          <ButtonToggle
            className={styles.alignmentToggle}
            label="Align to Canvas"
            hideLabel
            clickOnly
            onSIChange={_alignToCanvas}
          >
            <Tooltip>
              <span slot="content">
                <Text>Align Left</Text>
              </span>
              <ButtonToggleOption
                className={styles.alignLeft}
                icon="alignCanvasLeft"
                value={ALIGN_LEFT}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Center</Text>
              </span>
              <ButtonToggleOption
                className={styles.alignCenter}
                icon="alignCanvasHorizCenter"
                value={ALIGN_CENTER}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Right</Text>
              </span>
              <ButtonToggleOption
                className={styles.alignRight}
                icon="alignCanvasRight"
                value={ALIGN_RIGHT}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Top</Text>
              </span>
              <ButtonToggleOption
                className={styles.alignTop}
                icon="alignCanvasTop"
                value={ALIGN_TOP}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Middle</Text>
              </span>
              <ButtonToggleOption
                className={styles.alignMiddle}
                icon="alignCanvasVertCenter"
                value={ALIGN_MIDDLE}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Bottom</Text>
              </span>
              <ButtonToggleOption
                className={styles.alignBottom}
                icon="alignCanvasBottom"
                value={ALIGN_BOTTOM}
              />
            </Tooltip>
          </ButtonToggle>
        </>
      )}

      <Text className="panel-subheading">Align to Selection</Text>
      <ButtonToggle
        className={styles.alignmentToggle}
        label="Align to Selection"
        hideLabel
        clickOnly
        onSIChange={_align}
      >
        <Tooltip>
          <span slot="content">
            <Text>Align Left</Text>
          </span>
          <ButtonToggleOption className={styles.alignLeft} icon="alignLeft" value={ALIGN_LEFT} />
        </Tooltip>
        <Tooltip>
          <span slot="content">
            <Text>Align Center</Text>
          </span>
          <ButtonToggleOption
            className={styles.alignCenter}
            icon="alignCenterHoriz"
            value={ALIGN_CENTER}
          />
        </Tooltip>
        <Tooltip>
          <span slot="content">
            <Text>Align Right</Text>
          </span>
          <ButtonToggleOption className={styles.alignRight} icon="alignRight" value={ALIGN_RIGHT} />
        </Tooltip>
        <Tooltip>
          <span slot="content">
            <Text>Align Top</Text>
          </span>
          <ButtonToggleOption className={styles.alignTop} icon="alignTop" value={ALIGN_TOP} />
        </Tooltip>
        <Tooltip>
          <span slot="content">
            <Text>Align Middle</Text>
          </span>
          <ButtonToggleOption
            className={styles.alignMiddle}
            icon="alignCenterVert"
            value={ALIGN_MIDDLE}
          />
        </Tooltip>
        <Tooltip>
          <span slot="content">
            <Text>Align Bottom</Text>
          </span>
          <ButtonToggleOption
            className={styles.alignBottom}
            icon="alignBottom"
            value={ALIGN_BOTTOM}
          />
        </Tooltip>
      </ButtonToggle>

      {!contained && (
        <>
          <Text className="panel-subheading">Distribute</Text>
          <ButtonToggle
            className={styles.alignmentToggle}
            label="Distribute"
            hideLabel
            clickOnly
            onSIChange={_distributeSpace}
          >
            <Tooltip>
              <span slot="content">
                <Text>Horizontal</Text>
              </span>
              <ButtonToggleOption
                className={styles.toggleIcon}
                icon="distHoriz"
                value={DISTRIBUTE_HORIZONTAL_SPACE}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Vertical</Text>
              </span>
              <ButtonToggleOption
                className={styles.toggleIcon}
                icon="distVert"
                value={DISTRIBUTE_VERTICAL_SPACE}
              />
            </Tooltip>
          </ButtonToggle>

          <Text className="panel-subheading">Match Size</Text>
          <ButtonToggle
            className={styles.alignmentToggle}
            label="Match Size"
            hideLabel
            clickOnly
            onSIChange={_matchSize}
          >
            <Tooltip>
              <span slot="content">
                <Text>Width</Text>
              </span>
              <ButtonToggleOption
                className={styles.toggleIcon}
                icon="matchWidth"
                value={MATCH_SIZE_WIDTH}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Height</Text>
              </span>
              <ButtonToggleOption
                className={styles.toggleIcon}
                icon="matchHeight"
                value={MATCH_SIZE_HEIGHT}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Width and Height</Text>
              </span>
              <ButtonToggleOption
                className={styles.toggleIcon}
                icon="matchHW"
                value={MATCH_SIZE_BOTH}
              />
            </Tooltip>
          </ButtonToggle>

          <ElementPropertiesPositionPanel />
        </>
      )}
    </PropertyPanelSectionWrapper>
  );
};

export default ElementPropertiesAlignmentPanel;
